<template>
  <div>
    <div class="activity-edit-content">
      <EditorMenuBubble :editor="editor">
        <div
          slot-scope="{ commands, isActive, menu }"
          class="menububble"
          :class="{ 'is-active': menu.isActive }"
          :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
        >
          <button
            class="menububble__button"
            :class="{ 'is-active': isActive.paragraph() }"
            @click="commands.paragraph"
          >
            <Icon name="paragraph" />
          </button>
          <button
            class="menububble__button"
            :class="{ 'is-active': isActive.bold() }"
            @click="commands.bold"
          >
            <Icon name="bold" />
          </button>
          <button
            class="menububble__button"
            :class="{ 'is-active': isActive.italic() }"
            @click="commands.italic"
          >
            <Icon name="italic" />
          </button>
          <button
            class="menububble__button"
            :class="{ 'is-active': isActive.heading({ level: 1 }) }"
            @click="commands.heading({ level: 1 })"
          >
            H1
          </button>
          <button
            class="menububble__button"
            :class="{ 'is-active': isActive.heading({ level: 2 }) }"
            @click="commands.heading({ level: 2 })"
          >
            H2
          </button>
          <button
            class="menububble__button"
            :class="{ 'is-active': isActive.heading({ level: 3 }) }"
            @click="commands.heading({ level: 3 })"
          >
            H3
          </button>
          <button
            class="menububble__button"
            :class="{ 'is-active': isActive.bullet_list() }"
            @click="commands.bullet_list"
          >
            <Icon name="list-ul" />
          </button>
          <button
            class="menububble__button"
            :class="{ 'is-active': isActive.ordered_list() }"
            @click="commands.ordered_list"
          >
            <Icon name="list-ol" />
          </button>
          <button
            class="menububble__button"
            :class="{ 'is-active': isActive.todo_list() }"
            @click="commands.todo_list"
          >
            <Icon name="list" />
          </button>
        </div>
      </EditorMenuBubble>
      <EditorContent id="editor-content" class="editor__content" :editor="editor"/>
      <div id="sellia-button-container" class="position-relative w-100">
        <SelliaButton
          v-if="!isEditing"
          :text="activity.content"
          @isRecordingType="$emit('isRecordingType', $event)"
        />
        <slot name="summary"></slot>
      </div>
    </div>
    <div class="suggestion-list" v-show="showSuggestions" ref="suggestions">
      <template v-if="hasResults">
        <div
          class="suggestion-list__item"
          :class="{ 'is-selected': navigatedUserIndex === index }"
          v-for="(user, index) in filteredUsers"
          :key="user.id"
          @click="selectUser(user)"
        >
          {{ user.name }} -
          <small>{{ user.email }}</small>
        </div>
      </template>
      <div v-else class="suggestion-list__item is-empty">
        {{ __('Nenhum usuário encontrado') }}.
      </div>
    </div>
  </div>
</template>

<script>
import tippy from 'tippy.js';
import Swal from 'sweetalert2';
import { Editor, EditorContent, EditorMenuBubble } from 'tiptap';
import {
  Blockquote,
  BulletList,
  HardBreak,
  Heading,
  ListItem,
  Mention,
  OrderedList,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Link,
  Strike,
  Underline,
  History,
  Placeholder,
} from 'tiptap-extensions';
import SummaryEdit from './SummaryEdit.vue';

export default {
  store: ['users'],

  components: {
    EditorMenuBubble,
    EditorContent,
    SummaryEdit,
  },

  props: {
    activity: {
      type: Object,
      required: true,
    },
    isEditing: {
      type: Boolean,
      required: false,
    },
    isTranscriptionEnabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      hasUnsavedChanges: false,
      query: null,
      lastKnownContent: null,
      editor: new Editor({
        autoFocus: true,
        extensions: [
          new Blockquote(),
          new BulletList(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new Mention({
            items: () => this.users,

            onEnter: ({ items, query, range, command, virtualNode }) => {
              this.query = query;
              this.filteredUsers = items;
              this.suggestionRange = range;
              this.renderPopup(virtualNode);
              this.insertMention = command;
            },

            onChange: ({ items, query, range, virtualNode }) => {
              this.destroyPopup();

              this.query = query;
              this.filteredUsers = items;
              this.suggestionRange = range;
              this.navigatedUserIndex = 0;
              this.renderPopup(virtualNode);
            },

            onExit: () => {
              this.query = null;
              this.filteredUsers = [];
              this.suggestionRange = null;
              this.navigatedUserIndex = 0;
              this.destroyPopup();
            },

            onKeyDown: ({ event }) => {
              if (event.keyCode === 38) {
                this.upHandler();
                return true;
              }

              if (event.keyCode === 40) {
                this.downHandler();
                return true;
              }

              if (event.keyCode === 13) {
                this.enterHandler();
                return true;
              }

              return false;
            },

            onFilter: (items, query) => {
              if (!query) {
                return items;
              }

              return _.filter(
                items,
                (user) =>
                  user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1,
              );
            },
          }),
          new ListItem(),
          new OrderedList(),
          new TodoItem(),
          new TodoList(),
          new Link(),
          new Bold(),
          new Code(),
          new Italic(),
          new Strike(),
          new Underline(),
          new History(),
          new Placeholder({
            emptyClass: 'is-empty',
            emptyNodeText: this.__('O que foi feito e qual o próximo passo?'),
          }),
        ],

        onUpdate: ({ getHTML }) => {
          let content = getHTML().replace(/<[^>]*>/g, '').trim();

          if (content === '') {
            content = '';
            this.editor.setContent('');
          }
          // eslint-disable-next-line vue/no-mutating-props
          this.activity.content = getHTML();
          this.emitInputEvent(getHTML());
        },
      }),
      filteredUsers: [],
      suggestionRange: null,
      navigatedUserIndex: 0,
      insertMention: () => {},
      selliaActionResponse: {type: 'hidden', message: ""},
      lastTranscriptLength: 0,
      isTranscribing: false,
      transcriptionIndex: 0,
      lastTranscriptionIndex: null,
    };
  },

  computed: {
    hasResults() {
      return this.filteredUsers.length;
    },

    showSuggestions() {
      return this.query || this.hasResults;
    },

    selliaActionStatus() {
      if (this.selliaActionResponse.type == 'finish') {
        return 'Criado';
      } else if (this.selliaActionResponse.type == 'info') {
        return 'Erro ao criar';
      } else {
        return 'Criando';
      }
    },
  },

  watch: {
    activity() {
      this.editor.setContent('', {
        emitUpdate: true,
      });
    },

    isTranscribing(value) {
      if (value) {
        this.editor.setOptions({
          editable: false,
        });

        this.$emit('transcribing', true);
      } else {
        this.editor.setOptions({
          editable: true,
        });

        this.$emit('transcribing', false);
      }
    },
  },

  mounted() {
    this.editor.setContent(this.activity.content);

    Bus.$on('selliaActionResponse', (response) => {
      if (!this.isTranscriptionEnabled) {
        return;
      }

      this.selliaActionResponse = response;
    });

    Bus.$on('selliaTranscript', (transcript) => {
      if (!this.isTranscriptionEnabled) {
        return;
      }

      this.handleTranscriptReceived(transcript);
    });

    Bus.$on('selliaTranscriptStart', () => {
      console.log('newTranscription');

      if (!this.isTranscriptionEnabled) {
        return;
      }

      this.transcriptionIndex += 1;
    });

    // Adicionar evento para quando a transcrição for finalizada
    Bus.$on('selliaTranscriptEnd', () => {
      if (!this.isTranscriptionEnabled) {
        return;
      }

      this.isTranscribing = false;
      this.lastTranscriptLength = 0;
    });

    this.$nextTick(() => {
      const editorContent = document.getElementById('editor-content');
      if (editorContent) {
        const summarizedTextContainer = document.getElementById('summarized-text-container');
        if (summarizedTextContainer) {
          editorContent.appendChild(summarizedTextContainer);
        }
      }
    });

    this.$parent.$on('activity-changed', (content) => {
      if (content === '') {
        this.hasUnsavedChanges = false;
      }
    });
    this.$parent.$on('attachments-hasChange', () => {
      this.hasUnsavedChanges = true;
    });

    this.$parent.$on('attachments-removeChange', () => {
      this.hasUnsavedChanges = false;
    });

    window.addEventListener('beforeunload', (event) => {
      if (this.hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    });

    Bus.$on('deal-modal-close', () => {
      this.hasUnsavedChanges = false;

      document.removeEventListener('click', this.handleBeforeUnload);
    });

    document.addEventListener('click', (event) => {
      const target = event.target.closest('a, button');
      if (target && target.hasAttribute('data-ignore-unsaved-check')) {
        return;
      }
      if (target && target.tagName === 'A' && this.hasUnsavedChanges) {
        event.preventDefault();
        this.handleBeforeUnload(() => {
          window.location.href = target.href;
        });
      }
    });
  },

  beforeDestroy() {
    this.isTranscribing = false;
    this.lastTranscriptLength = 0;
    
    if (this.isTranscriptionEnabled) {
      Bus.$off('selliaActionResponse');
      Bus.$off('selliaTranscript');
      Bus.$off('selliaTranscriptEnd');
      Bus.$off('selliaTranscriptStart');
    }
    
    // Destruir o editor de forma segura
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }

    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    document.removeEventListener('click', this.handleBeforeUnload);
  },

  methods: {
    handleTranscriptReceived(transcript) {
      if (!transcript) return;
      
      try {
        // Verificar se o editor está pronto para receber atualizações
        if (!this.editor || !this.editor.view) {
          console.warn('Editor não está pronto para receber transcrição');
          return;
        }
        
        // Obter o conteúdo atual do editor de forma segura
        let currentContent = '';
        try {
          currentContent = this.editor.getHTML().replace(/<[^>]*>/g, '').trim();
          // currentContent = this.editor.getText();
        } catch (e) {
          console.warn('Erro ao obter conteúdo atual do editor:', e);
          currentContent = '';
        }
        
        // Se estamos no meio de uma transcrição, remover a parte anterior
        if (this.isTranscribing && this.transcriptionIndex === this.lastTranscriptionIndex) {
          // Remover a última transcrição parcial de forma segura
          if (this.lastTranscriptLength > 0 && currentContent.length >= this.lastTranscriptLength) {
            currentContent = currentContent.substring(0, currentContent.length - this.lastTranscriptLength);
          }
        } else {
          // Iniciar uma nova sessão de transcrição
          this.isTranscribing = true;
          
          // Adicionar espaço se necessário
          if (currentContent && !currentContent.endsWith(' ')) {
            currentContent += ' ';
          }
        }

        this.lastTranscriptionIndex = this.transcriptionIndex;
        
        // Usar transaction para atualizar o conteúdo de forma mais segura
        const newContent = '<p>' + currentContent + transcript + '</p>';
        
        // Atualizar o conteúdo com a nova transcrição usando setTimeout para evitar conflitos
        setTimeout(() => {
          try {
            this.editor.setContent(newContent);
            
            // Armazenar o tamanho da transcrição atual
            this.lastTranscriptLength = transcript.length;
            
            // Posicionar o cursor no final do texto
            this.editor.focus();
            
            // Atualizar o conteúdo da atividade
            this.activity.content = this.editor.getHTML();
            
            // Emitir manualmente o evento de input para atualizar o ActivityItem
            this.emitInputEvent(this.editor.getHTML());
          } catch (e) {
            console.error('Erro ao atualizar conteúdo do editor:', e);
          }
        }, 50);
        
      } catch (error) {
        console.error('Erro ao processar transcrição:', error);
      }
    },

    emitInputEvent(content) {
      const plainText = content
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
      if (this.plainText !== this.lastKnownContent && plainText !== '') {
        this.hasUnsavedChanges = true;
      } else {
        this.hasUnsavedChanges = false;
      }
      this.activity.content = content;
      this.$emit('input', content);
    },

    upHandler() {
      this.navigatedUserIndex =
        (this.navigatedUserIndex + this.filteredUsers.length - 1) %
        this.filteredUsers.length;
    },

    downHandler() {
      this.navigatedUserIndex =
        (this.navigatedUserIndex + 1) % this.filteredUsers.length;
    },

    enterHandler() {
      const user = this.filteredUsers[this.navigatedUserIndex];

      if (user) {
        this.selectUser(user);
      }
    },

    selectUser(user) {
      this.insertMention({
        range: this.suggestionRange,
        attrs: {
          id: user.hashid,
          label: `${user.name} `,
        },
      });

      this.editor.focus();
    },

    renderPopup(node) {
      if (this.popup) {
        return;
      }

      this.popup = tippy(node, {
        content: this.$refs.suggestions,
        trigger: 'mouseenter',
        interactive: true,
        theme: 'dark',
        placement: 'top-start',
        performance: true,
        inertia: true,
        duration: [400, 200],
        showOnInit: true,
        arrow: true,
        arrowType: 'round',
      });
    },

    destroyPopup() {
      if (this.popup) {
        this.popup.destroyAll();
        this.popup = null;
      }
    },

    async saveActivity() {
      try {
        await this.$axios.post('/activity', {
          content: this.activity.content,
        });
        this.hasUnsavedChanges = false;
      } catch (error) {
        console.error('Erro ao salvar a atividade:', error);
      }
    },

    async handleBeforeUnload(continueNavigation) {
      await this.saveActivity();
      if (this.hasUnsavedChanges) {
        Swal.fire({
          iconHtml: '<img src="/build/icons/question-circle.svg" style="width: 72px; height: 72px;">',
          title: this.__('Deseja realmente sair?'),
          html: `
            <div class="container-basic mb-3">
              <span class="container-basic-title">${this.__('Existem alterações que não foram salvas.')}</span>
            </div>
          `,
          buttonsStyling: false,
          allowOutsideClick: false,
          confirmButtonText: this.__('Sair'),
          cancelButtonColor: 'transparent',
          cancelButtonText: this.__('Cancelar'),
          showCancelButton: true,
          customClass: {
            container: 'container-delete-lead',
            confirmButton: 'salesflow-btn salesflow-btn-secondary',
            cancelButton: 'btn swal2-btn-cancel-transparent swal2-btn-full-line',
            icon: 'no-border',
          },
        }).then((result) => {
          if (result.isConfirmed) {
            this.hasUnsavedChanges = false;
            if (typeof continueNavigation === 'function') {
              continueNavigation();
            }
          }
        });
      } else if (typeof continueNavigation === 'function') {
        continueNavigation();
      }
    },
  },
};
</script>
<style lang="scss">
@import '../../../../node_modules/tippy.js/dist/tippy.css';
@import '../../../sass/variables';

.mention {
  background: rgba($black, 0.1);
  color: rgba($black, 0.6);
  font-size: 0.8rem;
  font-weight: bold;
  border-radius: 5px;
  padding: 0.2rem 0.5rem;
  white-space: nowrap;
}

.mention-suggestion {
  color: rgba($black, 0.6);
}

.suggestion-list {
  padding: 0.2rem;
  border: 2px solid rgba($black, 0.1);
  font-size: 0.8rem;
  font-weight: bold;

  &__no-results {
    padding: 0.2rem 0.5rem;
  }

  &__item {
    border-radius: 5px;
    padding: 0.2rem 0.5rem;
    margin-bottom: 0.2rem;
    cursor: pointer;

    &:last-child {
      margin-bottom: 0;
    }

    &.is-selected,
    &:hover {
      background-color: rgba($white, 0.2);
    }

    &.is-empty {
      opacity: 0.5;
    }
  }
}

.tippy-tooltip.dark-theme {
  background-color: $black;
  padding: 0;
  font-size: 1rem;
  text-align: inherit;
  color: $white;
  border-radius: 5px;

  .tippy-backdrop {
    display: none;
  }

  .tippy-roundarrow {
    fill: $black;
  }

  .tippy-popper[x-placement^='top'] & .tippy-arrow {
    border-top-color: $black;
  }

  .tippy-popper[x-placement^='bottom'] & .tippy-arrow {
    border-bottom-color: $black;
  }

  .tippy-popper[x-placement^='left'] & .tippy-arrow {
    border-left-color: $black;
  }

  .tippy-popper[x-placement^='right'] & .tippy-arrow {
    border-right-color: $black;
  }
#lead-modal {
  #options-button {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-dialog {
    width: 100%;
    max-width: 1100px;
    color: $salesflow-font-dark;
    border-radius: 16px;

    .salesflow-btn {
      margin-top: 8px;
      padding: 8px 24px;
    }

    .modal-main-content {
      width: 80%;
      overflow-y: scroll;
      border-right: 4px solid transparent;
      padding: 40px 16px 0px;
      border-bottom-right-radius: 16px;
    }

    .modal-sidebar {
      width: 20%;
      overflow-y: hidden;
      overflow-x: hidden;
      padding: 40px 40px 56px;
      color: $salesflow-font-dark;
      font-size: 0.75rem;
      line-height: 125%;
      font-weight: 400;
      display: flex;
      flex-direction: column;
      align-items: center;
      border: 1px solid $salesflow-outline-gray-medium;
      border-bottom-left-radius: 16px;
    }

    .modal-content {
      background-color: transparent;
      border-radius: 16px;

      .modal-body {
        min-height: 380px;
        padding: 0px;
        background-color: transparent;

        .col-left {
          z-index: 2;
          padding-left: 20px;
          padding-right: 30px;
        }

        .col-right {
          padding-left: 0px;
          margin-left: -25px;
        }
      }
    }
  }
  }
}

.lh-21 {
  line-height: 21px !important;
} 

.lh-18 {
  line-height: 18px !important;
}

.fs-8 {
  font-size: 12px !important;
}

.fs-7 {
  font-size: 14px !important;
}

.fw-600 {
  font-weight: 600 !important;
}

.fw-500 {
  font-weight: 500 !important;
}

.text-custom-purple {
  color: #5F33FF !important;
}

.gap-1 {
  gap: 4px !important;
}

.text-custom-dark-grey {
  color: #3A3B45 !important;
}
</style>
